/**
 * The notification chime (见 23 声音).
 *
 * Copied from the previous chat client's receive sound (`public/media/receive.mp3`).
 * Played from the workbench renderer rather than handed to the OS toast: a
 * Windows toast `<audio>` only accepts a built-in sound or a packaged-app path,
 * which an NSIS-installed Electron app cannot supply.
 *
 * Bytes are fetched and played from a blob URL. The packaged page is served
 * from `hibro-app://`, and a media element pointed at that URL can be rejected
 * (the protocol handler does not answer Range requests). Same approach as the
 * call ringtone.
 */

const NOTIFICATION_SOUND_URL = new URL(
  '../assets/notification-receive.mp3',
  import.meta.url,
).href;

let audio: HTMLAudioElement | undefined;
let loading: Promise<HTMLAudioElement | undefined> | undefined;

function logSound(message: string, error: unknown): void {
  const err = error instanceof Error ? error : new Error(String(error));
  const hibroLog = (
    globalThis as {
      hibroLog?: {
        append: (entry: {
          level: string;
          kind: string;
          scope: string;
          message: string;
          error?: { message: string };
        }) => void;
      };
    }
  ).hibroLog;
  hibroLog?.append({
    level: 'warn',
    kind: 'log',
    scope: 'notification',
    message,
    error: { message: err.message },
  });
}

function loadAudio(): Promise<HTMLAudioElement | undefined> {
  loading ??= (async () => {
    try {
      const res = await fetch(NOTIFICATION_SOUND_URL);
      if (!res.ok) {
        throw new Error(`notification sound fetch status=${res.status}`);
      }
      const bytes = await res.arrayBuffer();
      const objectUrl = URL.createObjectURL(
        new Blob([bytes], { type: 'audio/mpeg' }),
      );
      audio = new Audio(objectUrl);
      return audio;
    } catch (error) {
      loading = undefined;
      logSound('notification sound failed to load', error);
      return undefined;
    }
  })();
  return loading;
}

/**
 * Play the chime from the start. A failed load is retried next time.
 *
 * `currentTime` throws `InvalidStateError` while `readyState` is still
 * `HAVE_NOTHING`. A fresh element is already at 0, so the first play skips
 * the seek; later plays rewind only after metadata has arrived.
 */
export function playNotificationSound(): void {
  void loadAudio().then((el) => {
    if (!el) {
      return;
    }
    if (el.readyState > 0) {
      el.currentTime = 0;
    }
    void el.play().catch((error: unknown) => {
      // Restarting the same element aborts the play already in flight. That is
      // the next chime taking over, not a failure.
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }
      logSound('notification sound failed to play', error);
    });
  });
}
